import { AuthError } from '@azure/msal-browser';

/**
 * Maps an MSAL or network error to a user-friendly message.
 *
 * Technical details (error code, message) are logged to the console for
 * developers. Tokens, authorization codes, and raw auth responses are
 * never logged.
 */
export function getAuthErrorMessage(error: unknown): string {
    if (error instanceof AuthError) {
        // Log technical details for developers — never log token values
        console.error('[Auth] MSAL error:', error.errorCode, '-', error.errorMessage);

        switch (error.errorCode) {
            case 'user_cancelled':
            case 'access_denied':
                return 'Sign in was cancelled. Please try again.';

            case 'consent_required':
                return 'Your organisation has not yet granted access to this application. Please contact your administrator.';

            case 'interaction_required':
            case 'login_required':
                return 'Your session has expired. Please sign in again.';

            case 'invalid_client':
            case 'unauthorized_client':
                return 'Application configuration error. Please contact support.';

            case 'redirect_uri_mismatch':
                return 'Application configuration error (redirect URI). Please contact support.';

            case 'temporary_unavailable':
            case 'service_unavailable':
            case 'temporarily_unavailable':
                return 'The sign-in service is temporarily unavailable. Please try again in a few minutes.';

            case 'network_error':
                return 'Unable to reach the sign-in service. Please check your internet connection.';

            default:
                return 'An unexpected error occurred during sign in. Please try again.';
        }
    }

    if (error instanceof Error) {
        console.error('[Auth] Unexpected error:', error.message);

        if (
            error.message.toLowerCase().includes('network') ||
            error.message.toLowerCase().includes('failed to fetch')
        ) {
            return 'Unable to reach the sign-in service. Please check your internet connection.';
        }
    }

    return 'An unexpected error occurred. Please try again.';
}
